import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Send, 
  RefreshCw, 
  Loader2,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Clock,
  Download,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { templateService } from '../../services/templateService';
import sseService from '../../services/sseService';
import TemplateModal from './TemplateModal';
import TemplateDetailsModal from './TemplateDetailsModal';
import TemplateSubmissionModal from './TemplateSubmissionModal';

const TemplateMyList = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isSubmissionModalOpen, setIsSubmissionModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0
  });
  const [isImporting, setIsImporting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState({});

  const { register, handleSubmit, watch, reset, setValue } = useForm({
    defaultValues: {
      search: '',
      status: '',
      category: '',
      language: ''
    }
  });

  const searchFilters = watch();
  const searchTerm = watch('search');
  const [currentFilters, setCurrentFilters] = useState({
    status: '',
    category: '',
    language: ''
  });

  // Load templates
  const loadTemplates = async (page = 0) => {
    try {
      setLoading(true);
      const response = await templateService.getTemplates(page, pagination.size);
      setTemplates(response.content || []);
      setPagination({
        page: response.number || 0,
        size: response.size || 10,
        totalElements: response.totalElements || 0,
        totalPages: response.totalPages || 0
      });
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  // Search templates
  const searchTemplates = async (filters) => {
    try {
      setLoading(true);
      const response = await templateService.searchTemplates({
        ...filters,
        page: 0,
        size: pagination.size
      });
      setTemplates(response.content || []);
      setPagination({
        page: 0,
        size: response.size || 10,
        totalElements: response.totalElements || 0,
        totalPages: response.totalPages || 0
      });
    } catch (error) {
      console.error('Error searching templates:', error);
      toast.error('Failed to search templates');
    } finally {
      setLoading(false);
    }
  };

  // Load templates on component mount
  useEffect(() => {
    loadTemplates();
  }, []);

  // Set up SSE connection for real-time updates
  useEffect(() => {
    // Get customer ID from auth context or localStorage
    // Use a valid UUID format for testing
    const customerId = localStorage.getItem('customerId') || '5003964b-5496-49d9-bbfc-303000649716';
    
    // Connect to SSE with customer ID
    sseService.connect(customerId);

    // Handle template updates
    const handleTemplateUpdate = (template) => {
      console.log('Template updated via SSE:', template);
      setTemplates(prevTemplates => 
        prevTemplates.map(t => 
          t.templateId === template.templateId ? template : t
        )
      );
      toast.success(`Template "${template.templateName}" status updated to ${template.templateStatus}`);
    };

    // Handle template creation
    const handleTemplateCreated = (template) => {
      console.log('Template created via SSE:', template);
      setTemplates(prevTemplates => [template, ...prevTemplates]);
      toast.success(`Template "${template.templateName}" created successfully`);
    };

    // Handle template deletion
    const handleTemplateDeleted = (data) => {
      console.log('Template deleted via SSE:', data);
      setTemplates(prevTemplates => 
        prevTemplates.filter(t => t.templateId !== data.templateId)
      );
      toast.success('Template deleted successfully');
    };

    // Handle connection status
    const handleConnectionStatus = (status) => {
      console.log('SSE connection status:', status);
      // Error is logged to console but not shown to users
      if (status.error) {
        console.error('SSE connection error:', status.error);
      }
    };

    // Add event listeners
    sseService.addEventListener('template-update', handleTemplateUpdate);
    sseService.addEventListener('template-created', handleTemplateCreated);
    sseService.addEventListener('template-deleted', handleTemplateDeleted);
    sseService.addEventListener('connection-failed', handleConnectionStatus);

    // Cleanup on unmount
    return () => {
      sseService.removeEventListener('template-update', handleTemplateUpdate);
      sseService.removeEventListener('template-created', handleTemplateCreated);
      sseService.removeEventListener('template-deleted', handleTemplateDeleted);
      sseService.removeEventListener('connection-failed', handleConnectionStatus);
      sseService.disconnect();
    };
  }, []);

  // Handle search form submission (for Filter button)
  const onSearchSubmit = (data) => {
    // Update current filters
    const newFilters = {
      status: data.status || '',
      category: data.category || '',
      language: data.language || ''
    };
    setCurrentFilters(newFilters);
    
    // Apply filters with current search term
    const filters = {
      search: searchTerm || undefined,
      status: data.status || undefined,
      category: data.category || undefined,
      language: data.language || undefined
    };
    searchTemplates(filters);
  };

  // Track if component has mounted to avoid initial search trigger
  const [hasMounted, setHasMounted] = useState(false);

  // Handle search term change with debouncing
  useEffect(() => {
    // Skip on initial mount to avoid duplicate API calls
    if (!hasMounted) {
      setHasMounted(true);
      return;
    }

    const debounceTimer = setTimeout(() => {
      // Apply search with current filter values
      const filters = {
        search: searchTerm || undefined,
        status: currentFilters.status || undefined,
        category: currentFilters.category || undefined,
        language: currentFilters.language || undefined
      };
      
      // Only search if there's a search term or filters are applied
      if (searchTerm || currentFilters.status || currentFilters.category || currentFilters.language) {
        searchTemplates(filters);
      } else {
        // If no search term and no filters, load all templates
        loadTemplates();
      }
    }, 200); // 200ms debounce delay

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]); // Only trigger on search term change

  // Handle template creation/update success
  const handleTemplateSuccess = () => {
    loadTemplates(pagination.page);
    setIsModalOpen(false);
    setEditingTemplate(null);
  };

  // Handle template edit
  const handleEdit = (template) => {
    // Only allow editing for DRAFT and REJECTED templates
    if (template.templateStatus !== 'DRAFT' && template.templateStatus !== 'REJECTED') {
      toast.error('Cannot edit template in ' + template.templateStatus + ' status');
      return;
    }
    
    navigate(`/templates/${template.templateId}/edit`);
  };

  // Handle template delete
  const handleDelete = async (template) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      setLoading(true);
      await templateService.deleteTemplate(template.templateId);
      loadTemplates(pagination.page);
      toast.success('Template deleted successfully');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    } finally {
      setLoading(false);
    }
  };

  // Handle template view
  const handleView = (template) => {
    setSelectedTemplate(template);
    setIsDetailsModalOpen(true);
  };

  // Handle template submission
  const handleSubmitToWhatsApp = (template) => {
    setSelectedTemplate(template);
    setIsDetailsModalOpen(false);
    setIsSubmissionModalOpen(true);
  };

  // Handle template resubmission
  const handleResubmitToWhatsApp = async (template) => {
    // Use window.confirm to avoid ESLint error
    if (!window.confirm('Are you sure you want to resubmit this template? This will delete the existing template from WhatsApp and create a new one.')) {
      return;
    }

    try {
      setLoading(true);
      await templateService.resubmitTemplateToWhatsApp(template.templateId);
      toast.success('Template resubmitted successfully');
      loadTemplates(pagination.page);
    } catch (error) {
      console.error('Error resubmitting template:', error);
      toast.error(error.response?.data?.message || 'Failed to resubmit template');
    } finally {
      setLoading(false);
    }
  };

  // Handle submission success
  const handleSubmissionSuccess = () => {
    loadTemplates(pagination.page);
    setIsSubmissionModalOpen(false);
  };

  // Handle import templates from WhatsApp
  const handleImportFromWhatsApp = async () => {
    if (!window.confirm('This will import all templates from your WhatsApp Business Manager. Continue?')) {
      return;
    }

    setIsImporting(true);
    try {
      const importedTemplates = await templateService.importTemplatesFromWhatsApp();
      toast.success(`Successfully imported ${importedTemplates.length} templates from WhatsApp`);
      loadTemplates(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to import templates from WhatsApp');
    } finally {
      setIsImporting(false);
    }
  };

  // Handle sync all templates with WhatsApp
  const handleSyncAllTemplates = async () => {
    if (!window.confirm('This will sync all templates with WhatsApp Business Manager. Continue?')) {
      return;
    }

    setIsSyncing(true);
    try {
      const syncedTemplates = await templateService.syncTemplatesWithWhatsApp();
      toast.success(`Successfully synced ${syncedTemplates.length} templates with WhatsApp`);
      loadTemplates(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to sync templates with WhatsApp');
    } finally {
      setIsSyncing(false);
    }
  };

  // Handle sync specific template with WhatsApp
  const handleSyncTemplate = async (template) => {
    setIsSyncing(true);
    try {
      await templateService.syncTemplateWithWhatsApp(template.templateId);
      toast.success(`Template "${template.templateName}" synced with WhatsApp`);
      loadTemplates(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to sync template with WhatsApp');
    } finally {
      setIsSyncing(false);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT': return 'bg-gray-100 text-gray-800';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED': return 'bg-green-100 text-green-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      case 'DISABLED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get action button for template
  const getActionButton = (template) => {
    switch (template.templateStatus) {
      case 'DRAFT':
        return (
          <button
            onClick={() => handleSubmitToWhatsApp(template)}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-md transition-colors"
            title="Submit to WhatsApp"
          >
            <Send className="h-3 w-3 mr-1" />
            Submit
          </button>
        );
      case 'REJECTED':
        return (
          <button
            onClick={() => handleResubmitToWhatsApp(template)}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-100 hover:bg-orange-200 rounded-md transition-colors"
            title="Resubmit to WhatsApp"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Resubmit
          </button>
        );
      case 'PENDING':
        return (
          <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-yellow-700 bg-yellow-100 rounded-md">
            <Clock className="h-3 w-3 mr-1" />
            Under Review
          </span>
        );
      case 'APPROVED':
        return (
          <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 rounded-md">
            ✓ Approved
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-8 pt-8 pb-6">
        <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Templates</h1>
          <p className="mt-1 text-sm text-gray-600">
            Create and manage your WhatsApp message templates
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => loadTemplates(pagination.page)}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleImportFromWhatsApp}
            disabled={isImporting}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 border border-blue-300 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <Download className={`h-4 w-4 mr-2 ${isImporting ? 'animate-spin' : ''}`} />
            {isImporting ? 'Importing...' : 'Import from WhatsApp'}
          </button>
          <button
            onClick={handleSyncAllTemplates}
            disabled={isSyncing}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-green-700 bg-green-100 border border-green-300 rounded-md hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Syncing...' : 'Sync All'}
          </button>
          <button
            onClick={() => navigate('/templates/create')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Template
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <form onSubmit={handleSubmit(onSearchSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  {...register('search')}
                  type="text"
                  placeholder="Search templates..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <select {...register('category')} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                <option value="">All Categories</option>
                <option value="AUTHENTICATION">Authentication</option>
                <option value="MARKETING">Marketing</option>
                <option value="UTILITY">Utility</option>
              </select>
              <select {...register('status')} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                <option value="">All Status</option>
                <option value="DRAFT">Draft</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="DISABLED">Disabled</option>
              </select>
              <select {...register('language')} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                <option value="">All Languages</option>
                <option value="en_US">English (US)</option>
                <option value="en_GB">English (UK)</option>
                <option value="es_ES">Spanish (Spain)</option>
                <option value="es_MX">Spanish (Mexico)</option>
                <option value="fr_FR">French</option>
                <option value="de_DE">German</option>
                <option value="it_IT">Italian</option>
                <option value="pt_BR">Portuguese (Brazil)</option>
                <option value="hi_IN">Hindi</option>
                <option value="ar_SA">Arabic</option>
              </select>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="submit" className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                <Filter className="h-4 w-4 mr-2" />
                Filter
              </button>
              <button
                type="button"
                onClick={() => {
                  reset();
                  setCurrentFilters({
                    status: '',
                    category: '',
                    language: ''
                  });
                  loadTemplates();
                }}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Clear
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Templates</h3>
            <div className="text-sm text-gray-500">
              {pagination.totalElements} templates total
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No templates</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new template.</p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/templates/create')}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
            {templates.map((template) => (
              <div key={template.templateId} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow flex flex-col h-64">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-lg font-medium text-gray-900 truncate">
                      {template.templateName}
                    </h4>
                    <p className="mt-1 text-sm text-gray-500">
                      {template.templateCategory} • {template.language}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(template.templateStatus)}`}>
                      {template.templateStatus}
                    </span>
                  </div>
                </div>

                <div className="flex-1 mb-4">
                  <p className="text-sm text-gray-600 line-clamp-4 h-16 overflow-hidden">
                    {template.content}
                  </p>
                </div>

                <div className="mt-auto">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Created {new Date(template.createdAt).toLocaleDateString()}
                    </div>
                    <div className="flex items-center space-x-2">
                      {getActionButton(template)}
                      <div className="relative group">
                        <button className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                          <div className="py-1">
                            <button
                              onClick={() => handleView(template)}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <Eye className="h-4 w-4 mr-3" />
                              View Details
                            </button>
                            {(template.templateStatus === 'DRAFT' || template.templateStatus === 'REJECTED') && (
                              <button
                                onClick={() => handleEdit(template)}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <Edit className="h-4 w-4 mr-3" />
                                Edit Template
                              </button>
                            )}
                            <button
                              onClick={() => handleSyncTemplate(template)}
                              disabled={isSyncing}
                              className="flex items-center w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50 disabled:opacity-50"
                            >
                              <RefreshCw className={`h-4 w-4 mr-3 ${isSyncing ? 'animate-spin' : ''}`} />
                              Sync with WhatsApp
                            </button>
                            <button
                              onClick={() => handleDelete(template)}
                              className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 mr-3 text-red-600" />
                              <span className="text-red-600">Delete</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {pagination.page * pagination.size + 1} to {Math.min((pagination.page + 1) * pagination.size, pagination.totalElements)} of {pagination.totalElements} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => loadTemplates(pagination.page - 1)}
                  disabled={pagination.page === 0}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </button>
                <button
                  onClick={() => loadTemplates(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages - 1}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {isModalOpen && (
        <TemplateModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingTemplate(null);
          }}
          template={editingTemplate}
          onSuccess={handleTemplateSuccess}
        />
      )}

      {isDetailsModalOpen && (
        <TemplateDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
          template={selectedTemplate}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onSubmitToWhatsApp={handleSubmitToWhatsApp}
          onResubmitToWhatsApp={handleResubmitToWhatsApp}
        />
      )}

      {isSubmissionModalOpen && (
        <TemplateSubmissionModal
          isOpen={isSubmissionModalOpen}
          onClose={() => setIsSubmissionModalOpen(false)}
          template={selectedTemplate}
          onSuccess={handleSubmissionSuccess}
        />
      )}
      </div>
    </div>
    </div>
  );
};

export default TemplateMyList;



